<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import {
        NotificationModal,
        PageLoading,
    } from "../../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../../Services/index.svelte";

    import { mbsAllItemsList } from "../../../../stores";

    import CodeSelectorSearchMbs from "./mbsComponents/CodeSelectorSearchMBS.svelte";
    import CodeSelectorManualMBSContainer from "./mbsComponents/CodeSelectorManualMBSContainer.svelte";

    import ChangeItemTypeModal from "./common/ChangeItemTypeModal.svelte";

    import {
        getText,
        showError,
        showException,
        showInfo,
    } from "../../../../Utils/utilFunctions.svelte";

    import { insuranceTypesAllowLimitedAmountOfMbsItems } from "../../../../Utils/utilConstants.svelte";

    import { getPublicHolidays } from "../../../../Utils/DataLoaders.svelte";

    export let itemCodeObj = {},
        doctorId = 0,
        latestObjDetails = {},
        procedureName = "",
        itemType = '',
        isSelectorOpen = false,
        itemsData = [], // items already selected
        hasValidBillingData = false,
        saveBillingItem = () => {},
        hasActiveQuoteOrInvoice = false,
        clearItems = () => {},
        changeItemType = () => {},
        closeModal = () => {},
        isTypeA = false,
        isSavingItems = false,
        allowAsaCodes = true,
        allowFreeTextCodes = false;

    $: if (isSelectorOpen) selectedItems = [...itemsData];
    else selectedItems = [];

    $: isAddBtnDisabled = _checkAddBtnDisabled(selectedItems, itemCodeObj);

    $: isLoadingData = isLoadingItems || isSyncingItems;

    const __AUTOCLOSE_NOTIFICATION_TIMEOUT__ = 3000,
        __AUTOCLOSE_T10_NOTIFICATION_TIMEOUT__ = 5000,
        __DEFAULT_ITEM_TYPE__ = Number(0),
        __MAX_NUM_ITEMS_TIMEOUT__ = 5000,
        __FLY_DURATION__ = 300;

    let isLoadingItems = true,
        isSyncingItems = true,
        selectedItems = [],
        holidaysInState = [],
        infoTypesMap = new Map();

    let timeObj = {};

    let isNotificationOpen = false,
        autoCloseTimer,
        autoCloseCoolDown = __AUTOCLOSE_NOTIFICATION_TIMEOUT__;

    let isT10NotificationOpen = false,
        is18216Or18226alreadyUsedModalOpenFunc = null, //this stored the ref of the save items function
        t10AutoCloseTimer,
        t10AutoCloseCoolDown = __AUTOCLOSE_T10_NOTIFICATION_TIMEOUT__;

    let isExceedMaxNumItemsNotificationOpen = false,
        maxNumItemsTimer,
        maxNumItemsCoolDown = __MAX_NUM_ITEMS_TIMEOUT__;

    let isT10WithOutsideT1andT6andT7andA11NotificationOpen = false,
        argsForT10WithOutsideT1andT6andT7andA11 = [];

    let isClearItemsConfirmationOpen = false,
        isClearingItems = false;

    let isSwitchItemTypeModalOpen = false,
        selectedItemType = __DEFAULT_ITEM_TYPE__;

    const notAllowedWithT10 = ["A11", "T7", "T1"];

    $: btnOptions = [
        {
            class: "btn !bg-white !shadow-none text-black",
            loading: isSavingItems || isClearingItems,
            text: "Clear all items",
            isHidden: hasActiveQuoteOrInvoice || selectedItems.length == 0,
            function: () => {
                isClearItemsConfirmationOpen = true;
            }
        },
        {
            class: "btn btn-secondary btn-error",
            loading: isSavingItems || isClearingItems,
            text: "Cancel",
            function: () => {
                closeModal();
            }
        },
        {
            class: "btn btn-secondary min-w-[100px]",
            loading: isAddBtnDisabled || isSavingItems || isClearingItems,
            text: isSavingItems ? "Saving" : "Save & Refresh",
            function: () =>
                saveItems(selectedItems, "add", itemCodeObj, timeObj),
        },
    ];

    $: headerOptions = [
        {
            optionText: "Change to use Free Text Items",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(2),
            isDisabled: hasValidBillingData || isLoadingData,
            isHidden: !allowFreeTextCodes,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
        {
            optionText: "Change to ASA Codes",
            iFont: "fa-solid fa-right-left",
            function: () => _openSwitchItemTypeModal(1),
            isDisabled:
                isLoadingData ||
                hasValidBillingData ||
                isTypeA,
            isHidden: !allowAsaCodes,
            disabledTooltip: `You cannot change codes when ${itemType} is already created`
        },
    ];

    onMount(async () => {
        infoTypesMap = new Map([
            ["Consults", []],
            ["Surgery", []],
            ["Modifiers", []],
            ["AdditionalProcedures", []],
            ["Time", []],
            ["Other", []], // infoType = ""
        ]);

        await loadData();

        if ($mbsAllItemsList.length) await _syncItemsByType();
    });

    const loadData = async () => {
        isLoadingItems = true;

        if (!$mbsAllItemsList.length) await loadMbsItems();

        await loadHolidays();

        isLoadingItems = false;
    };

    const loadMbsItems = async () => {
        await apiMethods.getMethod
            .getMBSItemsWithoutRebate(0, 999999)
            .then((res) => _handleLoadItems(res))
            .catch((err) => showException(err));
    };

    const loadHolidays = async () => {
        const holidayObj = {
            "filter[year]": itemCodeObj.scheduledDate
                ? new Date(itemCodeObj.scheduledDate).getFullYear()
                : new Date().getFullYear(),
        };

        if (itemCodeObj.patientState)
            holidayObj["filter[state]"] = itemCodeObj.patientState;

        await getPublicHolidays(0, 999999, holidayObj)
            .then((res) => _handleLoadHolidays(res))
            .catch((err) => showException(err));
    };

    const updateItems = (arr = []) => {
        const { insuranceType } = itemCodeObj;

        if (arr.length <= 14) selectedItems = [...arr];
        else if (
            insuranceTypesAllowLimitedAmountOfMbsItems.includes(insuranceType)
        ) {
            openMaxNumItemsNotification();
            selectedItems = arr.slice(0, 14);
        } else selectedItems = [...arr];
    };

    const saveItems = (...args) => {
        const { billingType, mbsItem18216UsedToday, mbsItem18226UsedToday, neuraxialType, startDate, endDate } = itemCodeObj;
        if(billingType == 2 && neuraxialType && !startDate && !endDate) {
            showInfo("In order to save items on a 'Labour epidural' encounter you need to set start and end times.");
            return;
        }

        //check if 18216 or 18226 used today already on a diff encounter (not happy with the implementation)
        if((args[0].some((item) => item.itemNum == 18216 || item.itemNum == 18226) && (mbsItem18216UsedToday || mbsItem18226UsedToday)) && args[args.length - 1] !== "skip18216or18226Check") {
            is18216Or18226alreadyUsedModalOpenFunc = () => {
                args.push("skip18216or18226Check");
                saveItems(...args)
            };
            return;
           }

        if (billingType != 0) {
            saveBillingItem(...args);
            return;
        }

        const hasT10Items = args[0].some((item) => item.group == "T10");

        if (!hasT10Items) openT10Notification();
        else {
            const notAllowedWithT10ItemsExists = args[0].some(
                ({ group }) => notAllowedWithT10.includes(group),
            );

            if (!notAllowedWithT10ItemsExists) {
                saveBillingItem(...args);
                return;
            }

            openT10Notification(true, ...args);
        }
    };

    const removeItemsNotAllowedWithT10 = () => {
        selectedItems = selectedItems.filter((item) =>
            !notAllowedWithT10.includes(item.group),
        ); //filter items and exclude A11, T1 snd T7

        argsForT10WithOutsideT1andT6andT7andA11[0] = selectedItems;

        saveBillingItem(...argsForT10WithOutsideT1andT6andT7andA11);

        closeT10Notification();
    };

    const openNotification = () => {
        isNotificationOpen && closeNotification();

        setTimeout(() => {
            isNotificationOpen = true;

            _updateCountdown();
        }, __FLY_DURATION__);
    };

    const closeNotification = () => {
        isNotificationOpen = false;

        clearInterval(autoCloseTimer);

        autoCloseCoolDown = __AUTOCLOSE_NOTIFICATION_TIMEOUT__;
    };

    const openT10Notification = (
        isOutsideT1andT6andT7andA11 = false,
        ...args
    ) => {
        isT10NotificationOpen && closeT10Notification();

        if (isOutsideT1andT6andT7andA11) {
            isT10WithOutsideT1andT6andT7andA11NotificationOpen = true;
            argsForT10WithOutsideT1andT6andT7andA11 = [...args];
        } else {
            setTimeout(() => {
                isT10NotificationOpen = true;

                _updateT10Countdown();
            }, __FLY_DURATION__);
        }
    };

    const closeT10Notification = () => {
        isT10NotificationOpen = false;
        isT10WithOutsideT1andT6andT7andA11NotificationOpen = false;

        argsForT10WithOutsideT1andT6andT7andA11 = [];

        clearInterval(t10AutoCloseTimer);

        t10AutoCloseCoolDown = __AUTOCLOSE_T10_NOTIFICATION_TIMEOUT__;
    };

    const openMaxNumItemsNotification = () => {
        isExceedMaxNumItemsNotificationOpen && closeMaxNumItemsNotification();

        setTimeout(() => {
            isExceedMaxNumItemsNotificationOpen = true;

            _updateMaxNumItemsCountdown();
        }, __FLY_DURATION__);
    };

    const closeMaxNumItemsNotification = () => {
        isExceedMaxNumItemsNotificationOpen = false;

        clearInterval(maxNumItemsTimer);

        maxNumItemsCoolDown = __MAX_NUM_ITEMS_TIMEOUT__;
    };

    const confirmSwitchItemType = (newItemType = Number(0)) => {
        // no items selected before - no latest data
        changeItemType(newItemType);
        _closeSwitchItemTypeModal();
    };

    // HELPER FUNCTIONS
    const _handleLoadItems = (res) => {
        if (!res || res.error) {
            showError(
                "Failed to load items, please refresh the page and try again and contact support if the issue persists.",
            );
            closeModal();
        } else $mbsAllItemsList = [...res.results];
    };

    const _handleLoadHolidays = (res) => {
        if (!res || res.error) {
            showError(
                "Failed to load holidays, please refresh the page and try again and contact support if the issue persists.",
            );
            closeModal();
        } else holidaysInState = [...res.results];
    };

    const _syncItemsByType = async () => {
        isSyncingItems = true;

        await $mbsAllItemsList.map((item) => {
            const arr = item.infoType
                ? infoTypesMap.get(item.infoType)
                : infoTypesMap.get("Other");

            if (arr) {
                arr.push(item);

                if (item.infoType) infoTypesMap.set(item.infoType, arr);
                else infoTypesMap.set("Other", arr);
            }

            if (selectedItems.length) {
                const { itemNum, subItemNum, infoType } = item;

                selectedItems.map((item) => {
                    if (
                        item.itemNum == itemNum &&
                        item.subItemNum == subItemNum &&
                        item.infoType == infoType
                    ) {
                        const findItem = $mbsAllItemsList.find(
                            (item) =>
                                item.itemNum == itemNum &&
                                item.subItemNum == subItemNum &&
                                item.infoType == infoType,
                        );
                        item.id = findItem.id;
                    }
                });
            }
        });

        isSyncingItems = false;
    };

    const _checkAddBtnDisabled = (itemsArr, itemProps) => {
        const { billingType, startDate, endDate, neuraxialType } = itemProps;

        if (itemsArr.length) return false;

        if (billingType != 2) return true;

        if ((!startDate || !endDate) && neuraxialType) return false;

        return true;
    };

    const _updateCountdown = () => {
        clearInterval(autoCloseTimer);

        const countdown = () => {
            autoCloseCoolDown -= 1000;

            if (autoCloseCoolDown <= 0) closeNotification();
        };

        autoCloseTimer = setInterval(countdown, 1000);
    };

    const _updateT10Countdown = () => {
        clearInterval(t10AutoCloseTimer);

        const countdown = () => {
            t10AutoCloseCoolDown -= 1000;

            if (t10AutoCloseCoolDown <= 0) closeT10Notification();
        };

        t10AutoCloseTimer = setInterval(countdown, 1000);
    };

    const _updateMaxNumItemsCountdown = () => {
        clearInterval(maxNumItemsTimer);

        const countdown = () => {
            maxNumItemsCoolDown -= 1000;

            if (maxNumItemsCoolDown <= 0) closeMaxNumItemsNotification();
        };

        maxNumItemsTimer = setInterval(countdown, 1000);
    };

    const _openSwitchItemTypeModal = (
        itemType = Number(__DEFAULT_ITEM_TYPE__),
    ) => {
        selectedItemType = Number(itemType);
        isSwitchItemTypeModalOpen = true;
    };

    const _closeSwitchItemTypeModal = () => {
        isSwitchItemTypeModalOpen = false;
        selectedItemType = Number(__DEFAULT_ITEM_TYPE__);
    };

    const getNotEligibleItemCodesForT10 = () => {
        let codes = selectedItems
            .filter(({ group }) => notAllowedWithT10.includes(group))
            .map(({ itemNum, group }) => `${itemNum} (Group ${group})`);

        return codes.join(", ");
    };
</script>

<NotificationModal
    isModalOpen={isSelectorOpen}
    title="Select MBS Codes"
    {closeModal}
    modalMaxWidth="max-w-6xl"
    cancelText="Cancel"
    showConfirmBtn={false}
    showCancelBtn={false}
    {btnOptions}
    {headerOptions}
    isConfirming={isSavingItems}
    closeModalFunction={closeModal}
    noContentPadding
>
    <div class="flex flex-col overflow-y-auto relative p-6">
        {#if isNotificationOpen}
            <div
                role="alert"
                class="alert shadow-lg absolute z-10"
                style="width: calc(100% - 1.5rem); left: 0.75rem; right: 0.75rem"
                transition:fly={{ duration: __FLY_DURATION__, y: -50 }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    class="stroke-info shrink-0 w-6 h-6"
                    ><path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path></svg
                >
                <p class="cursor-default">
                    No further claiming of this item number is allowed. ONLY 1
                    allowed per patient per calendar day.
                </p>
                <button
                    class="btn btn-secondary btn-sm"
                    on:click={closeNotification}
                >
                    Close (close in {Math.floor(autoCloseCoolDown / 1000)} s)
                </button>
            </div>
        {/if}

        {#if isT10NotificationOpen || isT10WithOutsideT1andT6andT7andA11NotificationOpen}
            <div
                role="alert"
                class="alert shadow-lg absolute z-10"
                style="width: calc(100% - 1.5rem); left: 0.75rem; right: 0.75rem"
                transition:fly={{ duration: __FLY_DURATION__, y: -50 }}
            >
                <div
                    class={`flex flex-1 w-full ${
                        isT10NotificationOpen
                            ? "flex-row gap-x-4 items-end"
                            : "flex-col gap-y-2"
                    }`}
                >
                    <div
                        class={`flex flex-1 w-full gap-x-4 ${
                            isT10NotificationOpen
                                ? ""
                                : "items-start justify-start"
                        }`}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            class="stroke-info shrink-0 w-6 h-6"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path></svg
                        >
                        <div
                            class="flex flex-1 flex-col gap-y-2 w-full items-start cursor-default"
                        >
                            {#if isT10NotificationOpen}
                                <p>
                                    T10 anaesthesia item is missing. You will
                                    not be able to submit the bill or quote.
                                </p>
                                <p>
                                    If you wish to bill T6 and/or T7 separately,
                                    please use the <strong
                                        >Consults & Attendances</strong
                                    > type of bill.
                                </p>
                            {:else}
                                <!-- <p>
                                    For anaesthesia encounters, items outside
                                    A11, T1, T6 and T7 cannot be co-claimed with
                                    a T10 item.
                                </p>
                                <p>
                                    Your current selection include items outside
                                    of A11, T1, T6 and T7. Anaemate can
                                    automatically remove these items from the
                                    selection.
                                </p>
                                <p>Do you want us to do it?</p> -->
                                <p>Medicare eligible anaesthesia services (MBS Group T10) are not co-claimable with items from Group A11, T1, or T7.</p>
                                <p>Your selection including the following ineligible items codes: {getNotEligibleItemCodesForT10()}.</p>
                                <p>These items must be removed, and can be claimed separately if required. Would you like to proceed?</p>
                            {/if}
                        </div>
                    </div>

                    <div
                        class={`${
                            isT10NotificationOpen
                                ? "flex items-end"
                                : "flex flex-1 w-full gap-x-4 items-center justify-end"
                        }`}
                    >
                        {#if isT10NotificationOpen}
                            <button
                                class="btn btn-secondary btn-sm"
                                on:click={closeT10Notification}
                            >
                                Close (close in {Math.floor(
                                    t10AutoCloseCoolDown / 1000,
                                )} s)
                            </button>
                        {:else}
                            <button
                                class="btn btn-secondary btn-sm"
                                on:click|preventDefault={closeT10Notification}
                            >
                                I will do it
                            </button>
                            <button
                                class="btn btn-secondary btn-sm"
                                on:click|preventDefault={removeItemsNotAllowedWithT10}
                            >
                                Yes, remove
                            </button>
                        {/if}
                    </div>
                </div>
            </div>
        {/if}

        {#if isExceedMaxNumItemsNotificationOpen}
            <div
                role="alert"
                class="alert flex items-end gap-x-2 shadow-lg absolute z-10"
                style="width: calc(100% - 1.5rem); left: 0.75rem; right: 0.75rem"
                transition:fly={{ duration: __FLY_DURATION__, y: -50 }}
            >
                <div class="flex flex-col gap-y-2 flex-1">
                    <div class="flex gap-x-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            class="stroke-info shrink-0 w-6 h-6"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path></svg
                        >
                        <p class="cursor-default">
                            Eclipse only allows a maximum of 14 MBS items per
                            Encounter. Consult and additional therapeutic and
                            diagnostic items can be submitted via a duplicated
                            "Consult & Attendance" Encounter.
                        </p>
                    </div>

                    <div class="flex w-full gap-x-2">
                        <div class="w-6 h-6" />
                        <p class="cursor-default">
                            If assistance is required, please contact support.
                        </p>
                    </div>
                </div>
                <div class="flex h-full items-end">
                    <button
                        class="btn btn-secondary btn-sm"
                        on:click|preventDefault={closeMaxNumItemsNotification}
                    >
                        Close (close in {Math.floor(maxNumItemsCoolDown / 1000)}
                        s)
                    </button>
                </div>
            </div>
        {/if}

        <p class="px-4 mt-2 font-bold">
            Procedure Name: {getText(itemCodeObj.procedureName)}
        </p>

        <CodeSelectorSearchMbs
            bind:itemProps={itemCodeObj}
            {selectedItems}
            {holidaysInState}
            {infoTypesMap}
            {updateItems}
            {openNotification}
        />

        <div class="divider px-4 my-0" />

        {#if isLoadingData}
            <PageLoading />
        {:else}
            <CodeSelectorManualMBSContainer
                bind:itemProps={itemCodeObj}
                bind:timeObj
                {doctorId}
                {procedureName}
                {selectedItems}
                {holidaysInState}
                {infoTypesMap}
                {updateItems}
                {openNotification}
            />
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={is18216Or18226alreadyUsedModalOpenFunc != null}
    title="Confirmation"
    closeModal={() => is18216Or18226alreadyUsedModalOpenFunc = null }
    cancelText="Cancel"
    submitText="Confirm"
    confirmAction={() => {
        //save items again without this check
        if(is18216Or18226alreadyUsedModalOpenFunc){
            is18216Or18226alreadyUsedModalOpenFunc();
        }
    }}
    closeModalFunction={() => is18216Or18226alreadyUsedModalOpenFunc = null }
>
    <div class="">
        Medicare does not permit claiming multiple epidural insertions for the same day. 
        <br/>A claim is likely to be rejected. Please consider using epidural top up codes instead. 
        <br/><br/>Are you sure you want to proceed?
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isClearItemsConfirmationOpen}
    title="Confirmation"
    closeModal={() => isClearItemsConfirmationOpen = false }
    cancelText="Cancel"
    submitText="Confirm"
    confirmAction={() => {
        //clear items
        isClearingItems = true;
        var clearItemsSuccessful = clearItems();
        isClearItemsConfirmationOpen = false; //close confirmation
        isClearingItems = false;
        if(clearItemsSuccessful) {
            closeModal(); //close code selector
        }
        
    }}
    closeModalFunction={() => isClearItemsConfirmationOpen = null }
>
    <div class="">
        This action will clear all {itemType} items.
        <br/>This means that also billing rule will be cleared and the {itemType} totals will be reset.
        <br/><br/>Are you sure you want to proceed?
    </div>
</NotificationModal>

<ChangeItemTypeModal
    isModalOpen={isSwitchItemTypeModalOpen}
    {selectedItemType}
    confirmAction={_closeSwitchItemTypeModal}
    closeModal={() => confirmSwitchItemType(selectedItemType)}
    closeModalFunction={_closeSwitchItemTypeModal}
/>
